import { useState } from "react";
import {
  Badge,
  Button,
  Card,
  Group,
  SegmentedControl,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { useDebouncedValue } from "@mantine/hooks";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AnalyticsConversion,
  AnalyticsConversionAttributionTier,
  AnalyticsConversionStatus,
  AnalyticsGoogleIngestStatus,
  PaginatedResponse,
} from "../../../api.ts";
import { ADMIN_LIST_PAGE_SIZE, adminErrorMessage, adminGet, adminSend } from "../../../api.ts";
import { notifyAdminError, notifyAdminSuccess } from "../../../lib/admin-notify.ts";
import { CursorPagination } from "../../../components/CursorPagination.tsx";
import { BotTimezoneNotice } from "../../../components/BotTimezoneNotice.tsx";
import { StatsTrendControls, type TrendRange } from "../../../components/StatsTrendControls.tsx";
import { todayInTz } from "@mip-tg-bot-turntable/utils";
import { BusinessDateTime } from "../../../components/BusinessDateTime.tsx";
import { formatDateTimeInTz } from "@mip-tg-bot-turntable/utils";
import { AdminTable } from "../../../components/AdminTable.tsx";
import { ANALYTICS_PLATFORM_FILTER_SEGMENTS } from "./platform-segments.ts";

const STATUS_LABELS: Record<AnalyticsConversionStatus, string> = {
  pending: "待上报",
  sent: "已上报",
  failed: "上报失败",
  skipped: "已跳过",
};

const STATUS_COLOR: Record<AnalyticsConversionStatus, string> = {
  pending: "orange",
  sent: "green",
  failed: "red",
  skipped: "gray",
};

const GOOGLE_INGEST_LABELS: Record<AnalyticsGoogleIngestStatus, string> = {
  processing: "入库处理中",
  success: "入库成功",
  failed: "入库失败",
  partial_success: "部分成功",
  stale: "无法查询",
};

const GOOGLE_INGEST_COLOR: Record<AnalyticsGoogleIngestStatus, string> = {
  processing: "yellow",
  success: "green",
  failed: "red",
  partial_success: "orange",
  stale: "gray",
};

const PAGE_SIZE = ADMIN_LIST_PAGE_SIZE;

const isRetryable = (item: AnalyticsConversion): boolean => {
  if (item.reportStatus === "failed" || item.reportStatus === "skipped") return true;
  if (item.reportStatus !== "sent" || item.platform !== "google") return false;
  if (item.googleIngestStatus === "processing") return false;
  if (
    item.googleIngestStatus === "failed" ||
    item.googleIngestStatus === "stale" ||
    item.googleIngestStatus === "partial_success" ||
    item.googleIngestStatus === null
  ) {
    return true;
  }
  // success：列表无法比对当前 ad ctId，仍展示重试按钮；后端 canRetry 会拦同 ctId
  return item.googleIngestStatus === "success";
};

const TIER_LABELS: Record<AnalyticsConversionAttributionTier, string> = {
  per_click: "Per-click",
  per_ad: "Per-ad",
};

const TIER_COLOR: Record<AnalyticsConversionAttributionTier, string> = {
  per_click: "blue",
  per_ad: "teal",
};

export function ConversionsTab(props: { readonly botId: string; readonly timezone: string }) {
  const { botId, timezone } = props;
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<AnalyticsConversionStatus | "all">("all");
  const [eventTypeFilter, setEventTypeFilter] = useState<"join" | "leave" | "all">("all");
  const [platformFilter, setPlatformFilter] = useState<
    "all" | "facebook" | "tiktok" | "google" | "sharechat"
  >("all");
  const [adIdInput, setAdIdInput] = useState("");
  const [debouncedAdId] = useDebouncedValue(adIdInput, 300);
  const [attemptsFilter, setAttemptsFilter] = useState<"all" | "gt1" | "retried">("all");
  const [range, setRange] = useState<TrendRange>("today");
  const [cursors, setCursors] = useState<Array<string | null>>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const cursor = cursors[pageIndex] ?? null;

  const resetPages = () => {
    setCursors([null]);
    setPageIndex(0);
  };

  const list = useQuery({
    queryKey: [
      "bot",
      botId,
      "analytics-conversions",
      range,
      statusFilter,
      eventTypeFilter,
      platformFilter,
      debouncedAdId,
      attemptsFilter,
      cursor,
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set("pageSize", String(PAGE_SIZE));
      params.set("range", range);
      if (cursor !== null) params.set("cursor", cursor);
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (eventTypeFilter !== "all") params.set("eventType", eventTypeFilter);
      if (platformFilter !== "all") params.set("platform", platformFilter);
      if (debouncedAdId.trim() !== "") params.set("adId", debouncedAdId.trim());
      if (attemptsFilter === "gt1") params.set("minAttempts", "2");
      if (attemptsFilter === "retried") params.set("retriedOnly", "true");
      return adminGet<PaginatedResponse<AnalyticsConversion>>(
        `/analytics/${botId}/conversions?${params.toString()}`,
      );
    },
  });

  const retryMut = useMutation({
    mutationFn: (conversionId: string) =>
      adminSend<void>("POST", `/analytics/${botId}/conversions/${conversionId}/retry`),
    onSuccess: () => {
      notifyAdminSuccess("已重新入队，等待上报任务处理");
      void queryClient.invalidateQueries({ queryKey: ["bot", botId, "analytics-conversions"] });
    },
    onError: (err) => notifyAdminError(err, "重试失败"),
  });

  const batchRequeueMut = useMutation({
    mutationFn: () =>
      adminSend<{ requeued: number }>("POST", `/analytics/${botId}/conversions/requeue-failed`, {
        ...(platformFilter !== "all" ? { platform: platformFilter } : {}),
        limit: 500,
      }),
    onSuccess: (data) => {
      notifyAdminSuccess(`已批量重入队 ${data.requeued} 条失败/跳过转化，等待 cron 上报`);
      void queryClient.invalidateQueries({ queryKey: ["bot", botId, "analytics-conversions"] });
    },
    onError: (err) => notifyAdminError(err, "批量重试失败"),
  });

  const rows = (list.data?.items ?? []).map((item) => (
    <Table.Tr key={item.id}>
      <Table.Td style={{ whiteSpace: "nowrap" }}>
        <BusinessDateTime iso={item.occurredAt} businessTimezone={timezone} />
      </Table.Td>
      <Table.Td>
        <Badge
          color={
            item.platform === "facebook"
              ? "blue"
              : item.platform === "google"
                ? "yellow"
                : item.platform === "sharechat"
                  ? "orange"
                  : "violet"
          }
          size="sm"
        >
          {item.platform}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="xs" ff="monospace">
          {item.adId}
        </Text>
      </Table.Td>
      <Table.Td>
        <Badge color={TIER_COLOR[item.attributionTier]} size="sm" variant="light">
          {TIER_LABELS[item.attributionTier]}
        </Badge>
      </Table.Td>
      <Table.Td>
        {item.pixelId !== null ? (
          <Text size="xs" ff="monospace">
            {item.pixelId}
          </Text>
        ) : (
          <Text size="xs" c="dimmed">
            -
          </Text>
        )}
      </Table.Td>
      <Table.Td>{item.tgUserId}</Table.Td>
      <Table.Td>
        <Badge color={item.eventType === "join" ? "green" : "orange"} size="sm" variant="light">
          {item.eventType === "join" ? "加群" : "退群"}
        </Badge>
      </Table.Td>
      <Table.Td>
        {item.reportedEventName !== null ? (
          <Text size="xs" ff="monospace">
            {item.reportedEventName}
          </Text>
        ) : (
          <Text size="xs" c="dimmed">
            -
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Badge color={STATUS_COLOR[item.reportStatus]} size="sm">
          {STATUS_LABELS[item.reportStatus]}
        </Badge>
      </Table.Td>
      <Table.Td>
        {item.platform === "google" && item.googleIngestStatus !== null ? (
          <Tooltip
            label={[
              item.googleRequestId !== null ? `requestId: ${item.googleRequestId}` : null,
              item.reportedConversionActionId !== null
                ? `上报 ctId: ${item.reportedConversionActionId}`
                : null,
              item.googleIngestCheckedAt !== null
                ? `检查: ${formatDateTimeInTz(item.googleIngestCheckedAt, timezone)}`
                : null,
              item.googleIngestDetail,
              "sent=HTTP 已接受；入库=Data Manager requestStatus；Ads 报表可能再延迟数小时",
            ]
              .filter((x): x is string => x !== null && x !== "")
              .join("\n")}
            multiline
            maw={480}
            withArrow
          >
            <Badge color={GOOGLE_INGEST_COLOR[item.googleIngestStatus]} size="sm" variant="light">
              {GOOGLE_INGEST_LABELS[item.googleIngestStatus]}
            </Badge>
          </Tooltip>
        ) : item.platform === "google" && item.reportStatus === "sent" ? (
          <Text size="xs" c="dimmed">
            待初始化
          </Text>
        ) : (
          <Text size="xs" c="dimmed">
            -
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        {item.platform === "google" && item.reportedConversionActionId !== null ? (
          <Text size="xs" ff="monospace" lineClamp={1} title={item.reportedConversionActionId}>
            {item.reportedConversionActionId}
          </Text>
        ) : (
          <Text size="xs" c="dimmed">
            -
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        {item.reportAttempts > 0 ? (
          <Group gap={4} wrap="nowrap">
            <Text size="xs">{item.reportAttempts} 次</Text>
            {item.manualRetriedAt !== null ? (
              <Badge size="xs" variant="outline" color="blue">
                已重试
              </Badge>
            ) : null}
          </Group>
        ) : item.manualRetriedAt !== null ? (
          <Badge size="xs" variant="outline" color="blue">
            已重试
          </Badge>
        ) : (
          <Text size="xs" c="dimmed">
            -
          </Text>
        )}
      </Table.Td>
      <Table.Td style={{ minWidth: 420, maxWidth: 520 }}>
        {item.lastError !== null ? (
          <Tooltip label={item.lastError} multiline maw={640} withArrow>
            <Text size="xs" c="red" style={{ wordBreak: "break-all", whiteSpace: "pre-wrap" }}>
              {item.lastError}
            </Text>
          </Tooltip>
        ) : (
          <Text size="xs" c="dimmed">
            -
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        {isRetryable(item) ? (
          <Button
            size="xs"
            variant="light"
            loading={retryMut.isPending && retryMut.variables === item.id}
            onClick={() => {
              modals.openConfirmModal({
                title: "确认重试上报",
                children: (
                  <Text size="sm">
                    {item.reportStatus === "sent"
                      ? "将按当前广告 ctId/凭据重新入队。若 ctId 未变且已入库成功，后端会拒绝。旧 Ads 转化不会撤销。"
                      : "将把该转化重置为待上报，使用当前平台凭据重新尝试。凭据无效时会再次失败。"}
                  </Text>
                ),
                labels: { confirm: "确认重试", cancel: "取消" },
                onConfirm: () => retryMut.mutate(item.id),
              });
            }}
          >
            重试
          </Button>
        ) : (
          <Text size="xs" c="dimmed">
            -
          </Text>
        )}
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Stack gap="md">
      <Stack gap={4}>
        <Group justify="space-between" wrap="wrap" gap="xs">
          <Text fw={600} size="lg">
            转化记录
          </Text>
          <BotTimezoneNotice timezone={timezone} statsDay={todayInTz(timezone)} range={range} />
        </Group>
        <Text size="xs" c="dimmed">
          列表时间为业务时区。转化上报状态；可按平台、广告 ID、状态筛选与重试。
        </Text>
        <SegmentedControl
          fullWidth
          value={platformFilter}
          onChange={(v) => {
            setPlatformFilter(v as "all" | "facebook" | "tiktok" | "google" | "sharechat");
            resetPages();
          }}
          data={[...ANALYTICS_PLATFORM_FILTER_SEGMENTS]}
        />
      </Stack>
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" mb="md" wrap="wrap" gap="sm">
          <Group gap="sm" wrap="wrap">
            <StatsTrendControls
              range={range}
              granularity="hour"
              onRangeChange={(r) => {
                setRange(r);
                resetPages();
              }}
              onGranularityChange={() => {}}
              hideGranularity
            />
            <TextInput
              size="xs"
              placeholder="广告 ID"
              value={adIdInput}
              onChange={(e) => {
                setAdIdInput(e.currentTarget.value);
                resetPages();
              }}
              style={{ width: 200 }}
            />
            <SegmentedControl
              size="xs"
              value={statusFilter}
              onChange={(v) => {
                setStatusFilter(v as AnalyticsConversionStatus | "all");
                resetPages();
              }}
              data={[
                { label: "全部", value: "all" },
                { label: "待上报", value: "pending" },
                { label: "已上报", value: "sent" },
                { label: "失败", value: "failed" },
                { label: "跳过", value: "skipped" },
              ]}
            />
            <Select
              size="xs"
              placeholder="事件类型"
              data={[
                { label: "全部", value: "all" },
                { label: "加入", value: "join" },
                { label: "退群", value: "leave" },
              ]}
              value={eventTypeFilter}
              onChange={(v) => {
                setEventTypeFilter((v ?? "all") as "join" | "leave" | "all");
                resetPages();
              }}
              style={{ width: 120 }}
              clearable={false}
            />
            <Select
              size="xs"
              placeholder="尝试次数"
              data={[
                { label: "全部", value: "all" },
                { label: "大于 1 次", value: "gt1" },
                { label: "曾手动重试", value: "retried" },
              ]}
              value={attemptsFilter}
              onChange={(v) => {
                setAttemptsFilter((v ?? "all") as "all" | "gt1" | "retried");
                resetPages();
              }}
              style={{ width: 130 }}
              clearable={false}
            />
            <Button
              size="xs"
              variant="light"
              color="orange"
              loading={batchRequeueMut.isPending}
              onClick={() => {
                modals.openConfirmModal({
                  title: "批量重试失败/跳过",
                  children: (
                    <Text size="sm">
                      将当前平台筛选下最多 500 条 failed/skipped
                      加群转化重入待上报。凭据未恢复或已删除时会再次失败。确认继续？
                    </Text>
                  ),
                  labels: { confirm: "确认重试", cancel: "取消" },
                  confirmProps: { color: "orange" },
                  onConfirm: () => batchRequeueMut.mutate(),
                });
              }}
            >
              批量重试失败
            </Button>
          </Group>
        </Group>
        {list.isLoading ? (
          <Stack gap="xs">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} h={40} radius="sm" />
            ))}
          </Stack>
        ) : list.isError ? (
          <Text c="red" size="sm" ta="center" py="lg">
            {adminErrorMessage(list.error)}
          </Text>
        ) : (
          <>
            <AdminTable minWidth={1840}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>时间</Table.Th>
                  <Table.Th>平台</Table.Th>
                  <Table.Th>广告 ID</Table.Th>
                  <Table.Th>归因档位</Table.Th>
                  <Table.Th>Pixel ID</Table.Th>
                  <Table.Th>TG 用户 ID</Table.Th>
                  <Table.Th>转化类型</Table.Th>
                  <Table.Th>上报事件名</Table.Th>
                  <Table.Th>上报状态</Table.Th>
                  <Table.Th>Google 入库</Table.Th>
                  <Table.Th>上报 ctId</Table.Th>
                  <Table.Th>尝试次数</Table.Th>
                  <Table.Th style={{ minWidth: 420 }}>错误信息</Table.Th>
                  <Table.Th>操作</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={14}>
                      <Text c="dimmed" size="sm" ta="center">
                        暂无记录
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  rows
                )}
              </Table.Tbody>
            </AdminTable>
            <CursorPagination
              pageSize={list.data?.pageSize ?? PAGE_SIZE}
              itemCount={list.data?.items.length ?? 0}
              hasMore={list.data?.hasMore === true}
              canGoPrev={pageIndex > 0}
              loading={list.isFetching}
              onPrev={() => setPageIndex((i) => Math.max(0, i - 1))}
              onNext={() => {
                const next = list.data?.nextCursor ?? null;
                if (next === null) return;
                setCursors((prev) => {
                  const head = prev.slice(0, pageIndex + 1);
                  return [...head, next];
                });
                setPageIndex((i) => i + 1);
              }}
            />
          </>
        )}
      </Card>
    </Stack>
  );
}
